import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MotionConfig } from 'framer-motion';
import * as Sentry from '@sentry/react';
import { ScenarioInput } from 'src/graphql/_generated';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { GradientTextAction } from 'src/common/GradientText';
import { RefreshSvg } from 'src/assets/dynamicSvg';
import { useRiskIntelGate } from './useRiskIntelGate';
import { GateStates } from './steps/GateStates';
import { RiskIntelProvider } from './RiskIntelProvider';
import { useRiskIntel } from './useRiskIntel';
import { useScrollOnReveal, scrollWizardToTop } from './useScrollOnReveal';
import { NarrativeStep } from './steps/NarrativeStep';
import { FrequencyStep } from './steps/FrequencyStep';
import { ConsequencesStep } from './steps/ConsequencesStep';
import { ReviewStep } from './steps/ReviewStep';
import { LoaderDots } from './comps/FetchingState';
import { SecondaryButton } from './comps/SuggestionButtons';

/** Resolves true on success (host closes the modal), false on error (review stays open). */
export type OnCreateScenario = (data: Partial<ScenarioInput>) => Promise<boolean>;

const Container = styled.div`
  width: 100%;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 40px;
  margin-bottom: 16px;
`;

const ConfirmCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TextButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.cflowerBlue};
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.prussianBlue};
  }
`;

const ResumeLoading = styled.div`
  display: flex;
  justify-content: center;
  padding: 64px 0;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 56px 24px;
  text-align: center;
`;

const noopCreate: OnCreateScenario = async () => false;

const RiskIntelWizard: React.FC<{ workspaceId?: string; onCreateScenario: OnCreateScenario }> = ({
  workspaceId,
  onCreateScenario,
}) => {
  const { session, resuming, error, reset, timedOut, retrySession, otherAssessmentSession, discardOtherSession } =
    useRiskIntel();
  const navigate = useNavigate();
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [discardingOther, setDiscardingOther] = useState(false);
  // Narrative is mid "Try again" after an accept — hide the (now-stale) frequency/consequence lanes
  // below until the user navigates back. Pure FE; the BE state is untouched and shows again on return.
  const [reattempting, setReattempting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll each lane into view as it unlocks; the async frequency lane gets a second push on data arrival.
  const hasFrequency = !!session?.selectedCycleId;
  // Gate the consequences lane on the chosen frequency OR the impacts-phase status (Edge E3) — a resume
  // that lands past frequency without chosenTimes/Years on the wire would otherwise strand the user.
  const hasConsequences =
    session?.frequency?.chosenValue != null ||
    session?.status === 'mining_impacts' ||
    session?.status === 'awaiting_impacts' ||
    session?.status === 'quantified';
  const frequencyRef = useScrollOnReveal(hasFrequency ? (session?.frequency ? 'loaded' : 'loading') : false);
  const consequencesRef = useScrollOnReveal(hasConsequences);

  // Review replaces the narrative→frequency→consequences stack rather than appending below it.
  const inReview = session?.status === 'review';

  // Entering or leaving review jumps the modal to the top so the tab bar is visible.
  const prevStatusRef = useRef(session?.status);
  useEffect(() => {
    const status = session?.status;
    if (status === 'review' || prevStatusRef.current === 'review') scrollWizardToTop(containerRef.current);
    prevStatusRef.current = status;
  }, [session?.status]);

  // reset() abandons the session and clears the resume pointer, returning to a blank narrative.
  const startOver = () => {
    reset();
    setConfirmingReset(false);
  };

  // Abandon the in-progress session that lives on another assessment, freeing the one-per-user slot.
  const handleDiscardOther = async () => {
    setDiscardingOther(true);
    try {
      await discardOtherSession();
    } finally {
      setDiscardingOther(false);
    }
  };

  return (
    <Container ref={containerRef}>
      <TopBar>
        <Poppins px={16} weight={600} color="prussianBlue" css="text-transform: uppercase;">
          AI Risk Creation
        </Poppins>
        {/* Only offer "Start New Risk" when there's a session to reset — hidden on the empty/fresh
            state and the other-assessment state (nothing to reset there). */}
        {!session ? null : confirmingReset ? (
          <ConfirmCluster>
            <Poppins px={14} color="cflowerBlue">
              Discard this scenario and start over?
            </Poppins>
            <SecondaryButton
              label="Start over"
              icon={<RefreshSvg size={16} />}
              whiteBg
              data-cy="ai-start-over-confirm"
              onClick={startOver}
            />
            <TextButton type="button" onClick={() => setConfirmingReset(false)}>
              Cancel
            </TextButton>
          </ConfirmCluster>
        ) : (
          <SecondaryButton
            label="Start New Risk"
            icon={<RefreshSvg size={16} />}
            whiteBg
            data-cy="ai-start-over"
            onClick={() => setConfirmingReset(true)}
          />
        )}
      </TopBar>
      {resuming && !session ? (
        <ResumeLoading>
          <LoaderDots />
        </ResumeLoading>
      ) : otherAssessmentSession && !session ? (
        <ErrorState>
          <Poppins px={18} weight={600} color="prussianBlue">
            AI risk creation already in progress
          </Poppins>
          <Poppins px={14} color="cflowerBlue" css="line-height: 1.7;">
            You can only generate one AI risk at a time. Discard your previous draft to start a new one here
            {otherAssessmentSession.assessmentId ? (
              <>
                , or{' '}
                <GradientTextAction
                  px={14}
                  weight={500}
                  data-cy="ai-goto-other-assessment"
                  onClick={() => navigate(`/risk-assessments/${otherAssessmentSession.assessmentId}?addScenario=ai`)}
                >
                  <span className="underline">return to draft</span>
                </GradientTextAction>{' '}
                to finish it.
              </>
            ) : (
              '.'
            )}
          </Poppins>
          <Spacer $px={8} />
          <SecondaryButton
            label="Discard Draft & Start Here"
            icon={<RefreshSvg size={16} />}
            whiteBg
            loading={discardingOther}
            data-cy="ai-discard-other"
            onClick={handleDiscardOther}
          />
        </ErrorState>
      ) : session?.status === 'errored' ? (
        <ErrorState>
          <Poppins px={18} weight={600} color="prussianBlue">
            Something went wrong
          </Poppins>
          <Poppins px={14} color="cflowerBlue" css="line-height: 1.7;">
            The risk analysis ran into a problem. Use “Start over” above to try again.
          </Poppins>
        </ErrorState>
      ) : timedOut ? (
        <ErrorState>
          <Poppins px={18} weight={600} color="prussianBlue">
            This is taking longer than expected
          </Poppins>
          <Poppins px={14} color="cflowerBlue" css="line-height: 1.7;">
            We haven’t heard back from the analysis yet. Check again, or use “Start over” above.
          </Poppins>
          <Spacer $px={8} />
          <TextButton type="button" onClick={retrySession} data-cy="ai-retry">
            <RefreshSvg size={16} />
            Try again
          </TextButton>
        </ErrorState>
      ) : inReview ? (
        <ReviewStep onCreateScenario={onCreateScenario} />
      ) : (
        <>
          <NarrativeStep workspaceId={workspaceId} onReattemptingChange={setReattempting} />
          {hasFrequency && !reattempting && (
            <div ref={frequencyRef}>
              <Spacer $px={32} />
              <FrequencyStep />
            </div>
          )}
          {hasConsequences && !reattempting && (
            <div ref={consequencesRef}>
              <Spacer $px={32} />
              <ConsequencesStep />
            </div>
          )}
        </>
      )}
      {error && (
        <>
          <Spacer $px={16} />
          <Poppins px={14} color="error" css="text-align: center; display: block;">
            {error}
          </Poppins>
        </>
      )}
    </Container>
  );
};

interface RiskIntelTabProps {
  assessmentId: string;
  assessmentName?: string;
  workspaceId?: string;
  onCreateScenario?: OnCreateScenario;
}

export const RiskIntelTab: React.FC<RiskIntelTabProps> = ({
  assessmentId,
  assessmentName,
  workspaceId,
  onCreateScenario = noopCreate,
}) => {
  const gate = useRiskIntelGate();

  // A render error degrades to the unavailable gate visual, keeping the rest of the modal usable.
  return (
    <MotionConfig reducedMotion="user">
      <Sentry.ErrorBoundary fallback={<GateStates state="unavailable" />}>
        {gate === 'enabled' ? (
          // Key by assessment so the provider re-initialises (resume + assessment match) when the user
          // lands on a different assessment (e.g. via "return to draft") without a full remount.
          <RiskIntelProvider key={assessmentId} assessmentId={assessmentId} assessmentName={assessmentName}>
            <RiskIntelWizard workspaceId={workspaceId} onCreateScenario={onCreateScenario} />
          </RiskIntelProvider>
        ) : (
          <GateStates state={gate} />
        )}
      </Sentry.ErrorBoundary>
    </MotionConfig>
  );
};
